/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { ReactNode, useState } from 'react'

import { useApi, useAuth } from '@/hooks'
import type { Company } from '@/types/dashboard-types'
import logger from '@/utils/logger'

import { CompanyContext } from './CompanyContext'

const log = logger({ category: 'CompanyProvider' })

export const CompanyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { api } = useApi()
  const { isAuthenticated } = useAuth()
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyId, setCompanyId] = useState<number>()
  const [isLoading, setIsLoading] = useState(true)

  React.useEffect(() => {
    if (!api || !isAuthenticated) {
      return
    }
    const fn = async () => {
      try {
        log.debug('Loading companies')
        const response = await api.get<Company[]>({
          path: '/company-list?skip=0&take=5000',
          origin: 'CompanyProvider',
        })
        log.debug('company response: %o', response)
        setIsLoading(false)
        if (response.error) {
          setCompanies([])
        } else {
          const data: Company[] = (response.data ?? []).filter((c) => !c.name.toLowerCase().includes('inactive'))
          if (data.length === 1) {
            setCompanyId(data[0].id)
          } else {
            const parent =
              data.find((c) => !c.parentCompany?.id) || // topmost parent
              data.find((c) => !data.some((pc) => pc.id === c.parentCompany?.id)) || // topmost parent within these results
              data[0] // any old company
            setCompanyId(parent.id)
          }
          setCompanies(data)
        }
      } catch (ex) {
        log.error('%o', ex)
        setIsLoading(false)
        setCompanies([])
      }
    }
    fn()
  }, [isAuthenticated])

  return (
    <CompanyContext.Provider value={{ companies, companyId, setCompanyId, isLoading }}>
      {children}
    </CompanyContext.Provider>
  )
}
